/**
 * TalentPerformanceTab.js — Phase 2: fetches from GET /api/hr/talent/performance
 */
import React, { useState, useEffect } from 'react';
import { Grid, Card, CardContent, Typography, Box, Chip, Tooltip as MuiTooltip, CircularProgress, Alert, FormControl, Select, MenuItem } from '@mui/material';
import { ComposedChart, Bar, Line, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Stars, School, Psychology, BarChart as BarChartIcon } from '@mui/icons-material';
import MetricCard from '../components/MetricCard';
import TrainingRecommendationsPanel from '../components/TrainingRecommendationsPanel';
import { talentApi } from '../services/hrmsApi';
import { useChatContext } from '../context/ChatContext';

const DEPARTMENTS_FALLBACK = ['IT', 'Sales', 'Finance', 'Operations', 'HR'];

function NineBoxGrid({ data }) {
  const potLabels  = ['Low Potential','Moderate Potential','High Potential'];
  const perfLabels = ['Low Performance','Core Performance','High Performance'];
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
          <Typography variant="caption" sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'text.secondary', fontWeight: 700, letterSpacing: '0.5px', fontSize: '0.68rem' }}>POTENTIAL →</Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          {[3,2,1].map((pot) => (
            <Box key={pot} sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
              <Typography variant="caption" sx={{ width: 85, color: 'text.secondary', fontSize: '0.65rem', display: 'flex', alignItems: 'center' }}>{potLabels[pot-1]}</Typography>
              {[1,2,3].map((perf) => {
                const cell = (data || []).find(c => c.perf === perf && c.pot === pot);
                return (
                  <Box key={perf} sx={{ flex: 1, height: 64, bgcolor: cell?.color || '#F5F7FA', borderRadius: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(0,0,0,0.07)', cursor: 'default', transition: 'transform 0.15s', '&:hover': { transform: 'scale(1.04)', zIndex: 2 } }}>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, color: cell?.textColor || '#333', textAlign: 'center', px: 0.5 }}>{cell?.label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: cell?.textColor || '#1565C0' }}>{cell?.count}</Typography>
                  </Box>
                );
              })}
            </Box>
          ))}
          <Box sx={{ display: 'flex', ml: '89px', gap: 0.5, mt: 0.5 }}>
            {perfLabels.map((l) => <Typography key={l} variant="caption" sx={{ flex: 1, textAlign: 'center', color: 'text.secondary', fontSize: '0.65rem' }}>{l}</Typography>)}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.5px', mt: 0.5 }}>PERFORMANCE →</Typography>
        </Box>
      </Box>
      <Chip label="🔵 AI Suggest: 12 candidates ready for promotion — Star & High Performer cells" size="small" sx={{ mt: 1.5, bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 600, height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }} />
    </Box>
  );
}

function TrainingWordCloud({ data }) {
  const maxSize = Math.max(...(data || [{ size: 1 }]).map(t => t.size));
  const colors  = ['#1565C0','#00897B','#AD1457','#6A1B9A','#E65100','#0277BD'];
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', pt: 1 }}>
      {(data || []).map((item, i) => {
        const color = colors[i % colors.length];
        return (
          <MuiTooltip key={item.text} title={`${item.size} request${item.size === 1 ? '' : 's'}`} arrow>
            <Chip label={item.text} size="small" sx={{ bgcolor: `${color}12`, color, fontWeight: 700, fontSize: `${0.68 + (item.size/maxSize)*0.3}rem`, height: 'auto', py: `${0.2 + (item.size/maxSize)*0.3}px`, border: `1px solid ${color}30`, cursor: 'default', '&:hover': { bgcolor: `${color}22` } }} />
          </MuiTooltip>
        );
      })}
    </Box>
  );
}

export default function TalentPerformanceTab() {
  const { openWithMessage } = useChatContext();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const [dept,            setDept]            = useState(null); // null until the department list resolves
  const [deptOptions,     setDeptOptions]     = useState(DEPARTMENTS_FALLBACK);
  const [skillGap,        setSkillGap]        = useState(null); // { dept, asOfDate, points }
  const [skillGapLoading, setSkillGapLoading] = useState(true);
  const [skillGapError,   setSkillGapError]   = useState(null);

  useEffect(() => {
    talentApi.getSummary()
      .then(setData)
      .catch((e) => setError(e.response?.data?.message || 'Failed to load talent data.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    talentApi.getSkillGapDepartments()
      .then((res) => {
        if (cancelled) return;
        const list = res?.departments?.length ? res.departments : DEPARTMENTS_FALLBACK;
        setDeptOptions(list);
        setDept((current) => current ?? list[0]);
      })
      .catch(() => {
        if (cancelled) return;
        // network/auth error — keep fallback list, still need an initial dept to fetch
        setDept((current) => current ?? DEPARTMENTS_FALLBACK[0]);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!dept) return; // wait for the department list to resolve the initial selection
    let cancelled = false;
    setSkillGapLoading(true);
    setSkillGapError(null);
    talentApi.getSkillGap(dept)
      .then((res) => { if (!cancelled) setSkillGap(res); })
      .catch((e) => {
        if (cancelled) return;
        setSkillGapError(e.response?.data?.message || 'Failed to load skill gap data.');
      })
      .finally(() => { if (!cancelled) setSkillGapLoading(false); });
    return () => { cancelled = true; };
  }, [dept]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;

  const { promotionReadyCount, trainingRequests, bellCurveData, nineBoxData, trainingWordCloud } = data;

  const skillGapPoints   = skillGap?.points || [];
  const skillGapDeptLabel = skillGap?.dept || dept;
  const avgRequired = skillGapPoints.length ? skillGapPoints.reduce((s, p) => s + p.required, 0) / skillGapPoints.length : null;
  const avgActual   = skillGapPoints.length ? skillGapPoints.reduce((s, p) => s + p.actual, 0)   / skillGapPoints.length : null;
  const skillGapSubtitle = !skillGapDeptLabel
    ? 'Loading department data…'
    : (avgRequired != null && avgActual != null)
      ? `${skillGapDeptLabel} Unit — ${avgActual.toFixed(1)}/${avgRequired.toFixed(1)} proficiency`
      : `${skillGapDeptLabel} Unit — proficiency data unavailable`;

  // Critical Skill Gap metric: the single largest required-vs-actual gap
  // within the selected department's Skill Gap Radar data — recomputed
  // whenever the department dropdown changes, rather than a static value.
  const worstSkillGap = skillGapPoints.reduce((worst, p) => {
    const gap = p.required - p.actual;
    return !worst || gap > worst.gap ? { skill: p.skill, gap } : worst;
  }, null);
  const criticalSkillGap = worstSkillGap ? Number(worstSkillGap.gap.toFixed(1)) : null;

  // Performance Distribution: Meets/Exceeds (ratings 3-5) vs. Below (ratings 1-2),
  // derived from the same bellCurveData feeding the chart below.
  const totalRatingCount = (bellCurveData || []).reduce((s, p) => s + p.count, 0);
  const belowCount = (bellCurveData || [])
    .filter(p => parseInt(p.rating, 10) <= 2)
    .reduce((s, p) => s + p.count, 0);
  const belowPct        = totalRatingCount ? Math.round((belowCount / totalRatingCount) * 100) : null;
  const meetsExceedsPct = belowPct != null ? 100 - belowPct : null;
  const perfDistValue    = belowPct != null ? `${meetsExceedsPct} / ${belowPct}` : '—';
  const perfDistSubtitle = totalRatingCount
    ? `Meets/Exceeds vs. Below — ${totalRatingCount.toLocaleString()} employees`
    : 'Meets/Exceeds vs. Below';

  // ── Dynamic MetricCard insight / status ──────────────────────────────────────
  const perfDistStatus  = belowPct == null ? 'blue'
    : belowPct <= 10 ? 'green'
    : belowPct <= 20 ? 'yellow'
    : 'red';
  const perfDistInsight = belowPct == null ? '🔵 Data unavailable'
    : belowPct <= 10 ? `🟢 Healthy distribution — only ${belowPct}% below target`
    : belowPct <= 20 ? `🟡 ${belowPct}% below target — monitor underperformers`
    : `🔴 ${belowPct}% below target — review underperformance drivers`;

  const promoStatus  = promotionReadyCount == null ? 'blue'
    : promotionReadyCount >= 12 ? 'green'
    : promotionReadyCount >= 7  ? 'yellow'
    : 'red';
  const promoInsight = promotionReadyCount == null ? '🔵 Data unavailable'
    : promotionReadyCount >= 12 ? `🟢 ${promotionReadyCount} candidates ready for promotion`
    : promotionReadyCount >= 7  ? `🟡 ${promotionReadyCount} candidates — build pipeline further`
    : `🔴 Low pipeline — only ${promotionReadyCount} promotion-ready`;

  const trainingStatus  = trainingRequests == null ? 'blue'
    : trainingRequests < 10 ? 'green'
    : trainingRequests <= 25 ? 'yellow'
    : 'red';
  const trainingInsight = trainingRequests == null ? '🔵 Data unavailable'
    : trainingRequests < 10 ? `🟢 Training backlog low (${trainingRequests} requests)`
    : trainingRequests <= 25 ? `🟡 ${trainingRequests} pending requests — prioritise approvals`
    : `🔴 High training backlog — ${trainingRequests} requests pending`;

  const skillGapStatus  = criticalSkillGap == null ? 'blue'
    : criticalSkillGap <= 1 ? 'green'
    : criticalSkillGap <= 3 ? 'yellow'
    : 'red';
  const skillGapInsight = criticalSkillGap == null ? '🔵 Data unavailable'
    : criticalSkillGap <= 1 ? '🟢 Skill gaps under control'
    : criticalSkillGap <= 3 ? `🟡 ${criticalSkillGap}-pt gap in ${worstSkillGap.skill} — targeted training needed`
    : `🔴 ${criticalSkillGap}-pt gap in ${worstSkillGap.skill} — immediate intervention`;

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} sm={6} lg={3}>
        <MetricCard title="Performance Distribution" value={perfDistValue} subtitle={perfDistSubtitle} icon={BarChartIcon} iconColor="#2E7D32" insight={perfDistInsight} status={perfDistStatus}
          onAskAI={() => openWithMessage(`Our performance distribution is ${meetsExceedsPct}% meeting or exceeding targets and ${belowPct}% below. Is this healthy and what should we do about the bottom ${belowPct}%?`)} />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <MetricCard title="Promotion-Ready" value={promotionReadyCount} subtitle="High Potential candidates" icon={Stars} iconColor="#1565C0" insight={promoInsight} status={promoStatus}
          onAskAI={() => openWithMessage(`We have ${promotionReadyCount} promotion-ready high-potential candidates. What is the recommended process for managing promotions and what risks should HR consider?`)} />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <MetricCard title="Training Requests" value={trainingRequests} subtitle="Pending this quarter" icon={School} iconColor="#F57F17" insight={trainingInsight} status={trainingStatus}
          onAskAI={() => openWithMessage(`There are ${trainingRequests} pending training requests this quarter, trending toward AI Tools and Cloud Management skills. How should HR prioritize training budget allocation?`)} />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <MetricCard title="Critical Skill Gap" value={worstSkillGap?.skill ?? null} valueSx={{ fontSize: '1.15rem', lineHeight: 1.3 }} subtitle={skillGapSubtitle} icon={Psychology} iconColor="#C62828" insight={skillGapInsight} status={skillGapStatus}
          onAskAI={() => openWithMessage(`In the ${skillGapDeptLabel} department, the largest skill gap is ${worstSkillGap ? `${worstSkillGap.skill} at ${criticalSkillGap} points (required vs. actual, out of 10)` : 'not yet available'}. What is the fastest way to close this gap — hire, train, or partner?`)} />
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={{ height: 380 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Performance Bell Curve</Typography>
            <Typography variant="caption" color="text.secondary">Rating distribution across organisation</Typography>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={bellCurveData} margin={{ top: 10, right: 5, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="rating" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: 16, paddingTop: 8 }} />
                <Bar yAxisId="left" dataKey="count" name="Employees" fill="#1565C0" radius={[6,6,0,0]} />
                <Line yAxisId="right" type="monotone" dataKey="pct" name="% of Total" stroke="#C62828" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={{ height: 380 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>Skill Gap Radar — {skillGapDeptLabel}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Required vs. actual competency (out of 10){skillGap?.asOfDate ? ` — as of ${skillGap.asOfDate}` : ''}
                </Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select value={dept ?? ''} onChange={(e) => setDept(e.target.value)}>
                  {deptOptions.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>

            {skillGapLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
            ) : skillGapError ? (
              <Alert severity="error" sx={{ mt: 1 }}>{skillGapError}</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={skillGapPoints} outerRadius="90%" margin={{ top: 8, right: 60, left: 60, bottom: 8 }}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="skill" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis domain={[0,10]} tick={{ fontSize: 10 }} tickCount={4} />
                  <Radar name="Required" dataKey="required" stroke="#C62828" fill="#C62828" fillOpacity={0.15} strokeWidth={2} />
                  <Radar name="Actual"   dataKey="actual"   stroke="#1565C0" fill="#1565C0" fillOpacity={0.25} strokeWidth={2} />
                  <Legend verticalAlign="bottom" align="center" wrapperStyle={{ paddingTop: 8 }} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={7}>
        <Card sx={{ height: 350 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Career 9-Box Grid — Succession Planning</Typography>
            <NineBoxGrid data={nineBoxData} />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={5}>
        <Card sx={{ height: 350 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Training Demand (This Quarter)</Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>Most requested courses from skill gap analysis</Typography>
            <TrainingWordCloud data={trainingWordCloud} />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <TrainingRecommendationsPanel />
      </Grid>
    </Grid>
  );
}
